import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import { APP_TIMEZONE } from '@/lib/timezone';
import {
  derivePotentialCriteria,
  type PotentialCriterionInput,
  type PotentialCriterionScore,
} from './potential-criteria';

/**
 * EU-686 (spec 068) — aba "Histórico de avaliações" do perfil do candidato.
 * Projeção mínima servida pelo BFF (proxy do App de Gestores); Zod espelho no
 * BFF — mudou lá → mude aqui.
 */
/**
 * Uma resposta da avaliação — o que o gestor escreveu ou pontuou. Só vem em
 * `kind: 'evaluation'`; a deliberação carrega `comment` + `result`.
 */
export interface CandidateEvaluationAnswer {
  key: string;
  label: string;
  type: 'rating' | 'text';
  value: string | number;
}

export interface CandidateEvaluationItem {
  kind: 'evaluation' | 'deliberation';
  programName: string | null;
  jobName: string | null;
  eventName: string | null;
  eventKind: 'dynamics' | 'interview' | null;
  managerName: string | null;
  comment: string | null;
  result: 'approved' | 'rejected' | 'on_hold' | null;
  values: CandidateEvaluationAnswer[];
  recordedAt: string;
  /**
   * DEV-286 — as cinco notas de potencial (chave + nota 1..5), só quando o
   * App de Gestores as gravou completas: ausente em avaliação legada, parcial
   * ou de tenant sem a feature. Sem rótulo: quem rotula é
   * `potential-criteria.ts`.
   */
  potentialCriteria?: PotentialCriterionInput[];
}

export interface CandidateEvaluationsResponse {
  items: CandidateEvaluationItem[];
}

export class CandidateEvaluationsError extends Error {
  readonly name = 'CandidateEvaluationsError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/**
 * Frescor curto (DEV-313). O que contém a tempestade de buscas (EU-686) é a
 * ausência de gatilho automático — foco, reconexão, intervalo — e o retry
 * limitado, não um frescor longo. Com uma hora, a lista vazia lida ANTES de o
 * gestor gravar a avaliação ficava presa até o F5; com 30 s, ir e voltar entre
 * abas ainda reaproveita o cache e reabrir a aba depois disso busca uma vez.
 */
const EVALUATIONS_STALE_TIME_MS = 30 * 1000;

/**
 * 4xx é resposta definitiva (o candidato não existe, o consultor não tem
 * acesso) — insistir só gera requisição inútil. 5xx e falha de rede merecem UMA
 * segunda chance, para que uma falha passageira não vire "Erro inesperado" na
 * cara do consultor. Duas tentativas no total: o suficiente para absorver o
 * soluço, longe da tempestade de quatro requisições que a QA observou.
 */
function retryOnlyTransient(failureCount: number, error: unknown): boolean {
  const status = error instanceof CandidateEvaluationsError ? error.status : 0;
  if (status >= 400 && status < 500) return false;
  return failureCount < 1;
}

export function candidateEvaluationsQueryOptions(userId: string, companyId?: string) {
  return queryOptions({
    // `companyId` entra na chave: o mesmo candidato pode ser visto em áreas de
    // trabalho diferentes, e o histórico é de UM tenant (spec 072).
    queryKey: ['candidate-evaluations', userId, companyId ?? null],
    queryFn: async ({ signal }) => {
      const res = await fetchClient<CandidateEvaluationsResponse>({
        method: 'GET',
        url: companyId
          ? `/candidates/${userId}/evaluations?companyId=${encodeURIComponent(companyId)}`
          : `/candidates/${userId}/evaluations`,
        signal,
      });
      if (res.error) {
        throw new CandidateEvaluationsError(
          `Failed to load candidate evaluations (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data as CandidateEvaluationsResponse;
    },
    // A aba busca ao abrir e para. Sem estes limites a query herdava os
    // defaults do TanStack Query — `staleTime: 0`, refetch ao focar a janela e
    // ao remontar, `retry: 3` — e o App refazia a mesma busca sozinho a cada
    // troca de aba ou volta de foco. Era isso que fazia a aba oscilar entre
    // "Sem avaliações" e "Erro inesperado" para o mesmo candidato.
    staleTime: EVALUATIONS_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: retryOnlyTransient,
  });
}

const EVENT_KIND_LABEL: Record<'dynamics' | 'interview', string> = {
  dynamics: 'Dinâmica',
  interview: 'Entrevista',
};

const RESULT_LABEL: Record<'approved' | 'rejected' | 'on_hold', string> = {
  approved: 'Aprovado(a)',
  rejected: 'Reprovado(a)',
  on_hold: 'Em espera',
};

const DATE_TIME_BR = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: APP_TIMEZONE,
});

/**
 * RN-11 — `dd/MM/aaaa - HH:mm`, instante ancorado em São Paulo (superfície de
 * autoria/consulta do consultor — exceção 2 do date-timezone-standard).
 */
export function formatEvaluationDateTime(iso: string): string | null {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const parts = DATE_TIME_BR.formatToParts(new Date(ms));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}/${get('month')}/${get('year')} - ${get('hour')}:${get('minute')}`;
}

export interface EvaluationField {
  label: string;
  value: string;
}

export interface EvaluationCardData {
  /** Chave estável de render (posição na listagem já ordenada pelo backend). */
  key: string;
  kind: 'evaluation' | 'deliberation';
  /** "Avaliação" | "Deliberação" (RN-08). */
  title: string;
  /** Grid na ordem RN-09/10; campo sem valor NÃO entra (RN-13). */
  fields: EvaluationField[];
  /** Comentário por extenso; null = label não renderiza (RN-13/14). */
  comment: string | null;
  /** Respostas da avaliação, rótulo e conteúdo. Vazio em deliberação. */
  answers: { label: string; value: string }[];
  /**
   * DEV-286 — as cinco linhas de critério na ordem canônica, ou `null` quando a
   * avaliação não as tem (legada / outro tenant / lista inválida): o card
   * aparece exatamente como hoje — sem bloco, sem estrela vazia (AC-19).
   */
  potentialCriteria: PotentialCriterionScore[] | null;
}

/**
 * Deriva o card da timeline (RN-08..13): ordem FIXA Programa, Vaga, Evento,
 * Tipo de evento, Responsável, Data e horário e — só em deliberação —
 * Resultado após Data e horário (Responsável UMA vez; a divergência do Figma
 * 15762-68765 foi resolvida pelo texto do card). Deliberação sem decisão
 * final exibe "Em espera" (HAV-008).
 */
export function deriveEvaluationCards(items: CandidateEvaluationItem[]): EvaluationCardData[] {
  return items.map((item, index) => {
    const fields: EvaluationField[] = [];
    const push = (label: string, value: string | null) => {
      if (value) fields.push({ label, value });
    };
    push('Programa', item.programName);
    push('Vaga', item.jobName);
    push('Evento', item.eventName);
    push('Tipo de evento', item.eventKind ? EVENT_KIND_LABEL[item.eventKind] : null);
    push('Responsável', item.managerName);
    push('Data e horário', formatEvaluationDateTime(item.recordedAt));
    if (item.kind === 'deliberation') {
      push('Resultado', RESULT_LABEL[item.result ?? 'on_hold']);
    }
    // Resposta sem rótulo ou sem conteúdo não vira bloco vazio na tela —
    // mesma regra do RN-13 aplicada aos campos de metadado.
    const answers = (item.values ?? []).flatMap((answer) => {
      const value = typeof answer.value === 'number' ? String(answer.value) : answer.value;
      if (!answer.label || !value) return [];
      return [{ label: answer.label, value }];
    });

    return {
      key: `${item.recordedAt}-${index}`,
      kind: item.kind,
      title: item.kind === 'deliberation' ? 'Deliberação' : 'Avaliação',
      fields,
      comment: item.comment,
      answers,
      // Deliberação é parecer+resultado, nunca carrega notas de critério.
      potentialCriteria:
        item.kind === 'evaluation' ? derivePotentialCriteria(item.potentialCriteria) : null,
    };
  });
}
